import { Ref, useCallback, useImperativeHandle, useRef, useState } from 'react';

import { InputApi } from '@cashier/cashier-model';

interface SelectItem {
  value: string;
  label: string;
}

interface UseSelectProps {
  ref?: Ref<InputApi>;
  options: SelectItem[];
  defaultValue?: string;
  onChange: (value: string) => void;
}

interface UseSelect {
  selectedItem: SelectItem | null;
  onItemSelected: (data: SelectItem) => void;
}

export const useSelect = ({
  ref,
  options,
  defaultValue,
  onChange,
}: UseSelectProps): UseSelect => {
  const [selectedItem, setSelectedItem] = useState<SelectItem | null>(
    defaultValue
      ? options.find(({ value }) => value === defaultValue) || {
          label: defaultValue,
          value: defaultValue,
        }
      : null,
  );

  const selectedItemRef = useRef<SelectItem | null>(selectedItem);

  const updateSelectedItem = useCallback((item: SelectItem | null) => {
    selectedItemRef.current = item;
    setSelectedItem(item);
  }, []);

  useImperativeHandle(ref, () => ({
    getValue: () => selectedItemRef.current?.value || '',
    setValue: (value) => {
      updateSelectedItem(
        options.find((item) => item.value === value) || {
          label: value,
          value: value,
        },
      );
    },
  }));

  const onItemSelected = (item: SelectItem): void => {
    updateSelectedItem(item);
    onChange(item.value);
  };

  return {
    selectedItem,
    onItemSelected,
  };
};
